<template>
  <div class="cache-management">
    <!-- 顶部操作栏 -->
    <div class="operation-bar">
      <el-row :gutter="20" type="flex" align="middle">
        <el-col :span="4">
          <el-input
            v-model="searchForm.share_key"
            placeholder="请输入分享码"
            clearable
            @clear="handleSearch"
            @keyup.enter.native="handleSearch"
          >
            <el-button slot="append" icon="el-icon-search" @click="handleSearch"></el-button>
          </el-input>
        </el-col>
        <el-col :span="4">
          <el-input
            v-model="searchForm.game_title"
            placeholder="请输入游戏标题"
            clearable
            @clear="handleSearch"
            @keyup.enter.native="handleSearch"
          >
            <el-button slot="append" icon="el-icon-search" @click="handleSearch"></el-button>
          </el-input>
        </el-col>
        <el-col :span="4">
          <el-input
            v-model="searchForm.file_name"
            placeholder="请输入文件名"
            clearable
            @clear="handleSearch"
            @keyup.enter.native="handleSearch"
          >
            <el-button slot="append" icon="el-icon-search" @click="handleSearch"></el-button>
          </el-input>
        </el-col>
        <el-col :span="12" style="text-align: right">
          <el-button-group>
            <el-button type="primary" icon="el-icon-plus" @click="handleAdd">添加缓存</el-button>
            <el-button type="danger" icon="el-icon-delete" @click="handleBatchDelete" :disabled="selectedIds.length === 0">批量删除</el-button>
            <el-button type="info" icon="el-icon-pie-chart" @click="handleStats">统计信息</el-button>
          </el-button-group>
        </el-col>
      </el-row>
    </div>

    <!-- 缓存列表 -->
    <div class="table-container">
      <el-table 
        :data="cacheList" 
        style="width: 100%" 
        v-loading="loading"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="55"></el-table-column>
        <el-table-column prop="share_key" label="分享码" width="120"></el-table-column>
        <el-table-column prop="file_id" label="文件ID" width="120"></el-table-column>
        <el-table-column prop="file_name" label="文件名" min-width="200">
          <template slot-scope="scope">
            <el-tooltip :content="scope.row.file_name" placement="top">
              <span class="file-name-text">{{ scope.row.file_name || '-' }}</span>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column prop="game_title" label="游戏标题" min-width="200">
          <template slot-scope="scope">
            <el-tooltip :content="scope.row.game_title" placement="top">
              <span class="game-title-text">{{ scope.row.game_title || '-' }}</span>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="创建时间" width="180">
          <template slot-scope="scope">
            {{ formatTime(scope.row.created_at) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button type="text" @click="handleView(scope.row)">查看</el-button>
            <el-button type="text" class="delete-button" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.currentPage"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pagination.pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total"
        >
        </el-pagination>
      </div>
    </div>

    <!-- 添加/编辑对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="60%"
      :before-close="handleDialogClose"
      class="cache-dialog"
    >
      <el-form :model="formData" :rules="rules" ref="cacheForm" label-width="120px">
        <el-form-item label="分享码" prop="share_key">
          <el-input v-model="formData.share_key" placeholder="请输入123网盘分享码"></el-input>
        </el-form-item>
        <el-form-item label="文件ID" prop="file_id">
          <el-input v-model="formData.file_id" placeholder="请输入文件ID"></el-input>
        </el-form-item>
        <el-form-item label="下载地址" prop="download_url">
          <el-input 
            v-model="formData.download_url" 
            type="textarea" 
            :rows="3"
            placeholder="请输入真实下载地址"
          ></el-input>
        </el-form-item>
        <el-form-item label="文件名">
          <el-input v-model="formData.file_name" placeholder="请输入文件名（可选）"></el-input>
        </el-form-item>
        <el-form-item label="游戏ID">
          <el-input-number v-model="formData.game_id" :min="1" placeholder="请输入游戏ID（可选）"></el-input-number>
        </el-form-item>
        <el-form-item label="游戏标题">
          <el-input v-model="formData.game_title" placeholder="请输入游戏标题（可选）"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="handleSubmit">确 定</el-button>
      </div>
    </el-dialog>

    <!-- 查看详情对话框 -->
    <el-dialog
      title="缓存详情"
      :visible.sync="viewDialogVisible"
      width="60%"
      class="view-dialog"
    >
      <el-descriptions :column="2" border>
        <el-descriptions-item label="ID">{{ viewData.id }}</el-descriptions-item>
        <el-descriptions-item label="分享码">{{ viewData.share_key }}</el-descriptions-item>
        <el-descriptions-item label="文件ID">{{ viewData.file_id }}</el-descriptions-item>
        <el-descriptions-item label="文件名">{{ viewData.file_name || '-' }}</el-descriptions-item>
        <el-descriptions-item label="游戏ID">{{ viewData.game_id || '-' }}</el-descriptions-item>
        <el-descriptions-item label="游戏标题">{{ viewData.game_title || '-' }}</el-descriptions-item>
        <el-descriptions-item label="创建时间" :span="2">{{ formatTime(viewData.created_at) }}</el-descriptions-item>
        <el-descriptions-item label="更新时间" :span="2">{{ formatTime(viewData.updated_at) }}</el-descriptions-item>
        <el-descriptions-item label="下载地址" :span="2">
          <div class="download-url-container">
            <el-input 
              :value="viewData.download_url" 
              type="textarea" 
              :rows="3" 
              readonly
            ></el-input>
            <div class="url-actions">
              <el-button type="primary" size="small" @click="copyDownloadUrl" icon="el-icon-copy-document">复制链接</el-button>
              <el-button type="success" size="small" @click="downloadFile" icon="el-icon-download">下载文件</el-button>
            </div>
          </div>
        </el-descriptions-item>
      </el-descriptions>
    </el-dialog>

    <!-- 统计信息对话框 -->
    <el-dialog
      title="缓存统计信息"
      :visible.sync="statsDialogVisible"
      width="50%"
      class="stats-dialog"
    >
      <div v-loading="statsLoading">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-card class="stats-card">
              <div class="stats-item">
                <div class="stats-number">{{ statsData.total || 0 }}</div>
                <div class="stats-label">缓存总数</div>
              </div>
            </el-card>
          </el-col>
          <el-col :span="12">
            <el-card class="stats-card">
              <div class="stats-item">
                <div class="stats-number">{{ statsData.recentWeek || 0 }}</div>
                <div class="stats-label">最近7天</div>
              </div>
            </el-card>
          </el-col>
        </el-row>
        <el-row :gutter="20" style="margin-top: 20px">
          <el-col :span="12">
            <el-card class="stats-card">
              <div class="stats-item">
                <div class="stats-number">{{ statsData.withGame || 0 }}</div>
                <div class="stats-label">有游戏关联</div>
              </div>
            </el-card>
          </el-col>
          <el-col :span="12">
            <el-card class="stats-card">
              <div class="stats-item">
                <div class="stats-number">{{ statsData.withoutGame || 0 }}</div>
                <div class="stats-label">无游戏关联</div>
              </div>
            </el-card>
          </el-col>
        </el-row>
        <div v-if="statsData.topGames && statsData.topGames.length > 0" style="margin-top: 20px">
          <h4>缓存数量最多的游戏</h4>
          <el-table :data="statsData.topGames" size="small">
            <el-table-column prop="game_title" label="游戏标题"></el-table-column>
            <el-table-column prop="cache_count" label="缓存数量" width="100"></el-table-column>
          </el-table>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { 
  getPan123CacheList, 
  createPan123Cache, 
  updatePan123Cache, 
  deletePan123Cache, 
  batchDeletePan123Cache,
  getPan123CacheStats 
} from '@/api';

export default {
  name: 'Pan123CacheManagement',
  data() {
    return {
      loading: false,
      cacheList: [],
      searchForm: {
        share_key: '',
        game_title: '',
        file_name: ''
      },
      pagination: {
        currentPage: 1,
        pageSize: 15,
        total: 0
      },
      selectedIds: [],
      dialogVisible: false,
      dialogTitle: '',
      isEdit: false,
      formData: {
        share_key: '',
        file_id: '',
        download_url: '',
        file_name: '',
        game_id: null,
        game_title: ''
      },
      rules: {
        share_key: [
          { required: true, message: '请输入分享码', trigger: 'blur' }
        ],
        file_id: [
          { required: true, message: '请输入文件ID', trigger: 'blur' }
        ],
        download_url: [
          { required: true, message: '请输入下载地址', trigger: 'blur' }
        ]
      },
      viewDialogVisible: false,
      viewData: {},
      statsDialogVisible: false,
      statsLoading: false,
      statsData: {}
    };
  },
  mounted() {
    this.fetchCacheList();
  },
  methods: {
    // 获取缓存列表
    async fetchCacheList() {
      this.loading = true;
      try {
        const params = {
          page: this.pagination.currentPage,
          pageSize: this.pagination.pageSize,
          ...this.searchForm
        };
        
        // 过滤空值
        Object.keys(params).forEach(key => {
          if (params[key] === '' || params[key] === null) {
            delete params[key];
          }
        });
        
        const response = await getPan123CacheList(params);
        if (response.success) {
          this.cacheList = response.data.caches;
          this.pagination.total = response.data.pagination.total;
        }
      } catch (error) {
        this.$message.error('获取缓存列表失败');
      } finally {
        this.loading = false;
      }
    },
    
    // 搜索
    handleSearch() {
      this.pagination.currentPage = 1;
      this.fetchCacheList();
    },
    
    // 分页大小改变
    handleSizeChange(val) {
      this.pagination.pageSize = val;
      this.pagination.currentPage = 1;
      this.fetchCacheList();
    },
    
    // 当前页改变
    handleCurrentChange(val) {
      this.pagination.currentPage = val;
      this.fetchCacheList();
    },
    
    // 选择改变
    handleSelectionChange(selection) {
      this.selectedIds = selection.map(item => item.id);
    },
    
    // 添加
    handleAdd() {
      this.dialogTitle = '添加缓存';
      this.isEdit = false;
      this.formData = {
        share_key: '',
        file_id: '',
        download_url: '',
        file_name: '',
        game_id: null,
        game_title: ''
      };
      this.dialogVisible = true;
    },
    
    // 编辑
    handleEdit(row) {
      this.dialogTitle = '编辑缓存';
      this.isEdit = true;
      this.formData = { ...row };
      this.dialogVisible = true;
    },
    
    // 查看
    handleView(row) {
      this.viewData = { ...row };
      this.viewDialogVisible = true;
    },
    
    // 删除
    handleDelete(row) {
      this.$confirm('确定要删除这条缓存记录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await deletePan123Cache(row.id);
          if (response.success) {
            this.$message.success('删除成功');
            this.fetchCacheList();
          }
        } catch (error) {
          this.$message.error('删除失败');
        }
      });
    },
    
    // 批量删除
    handleBatchDelete() {
      if (this.selectedIds.length === 0) {
        this.$message.warning('请选择要删除的记录');
        return;
      }
      
      this.$confirm(`确定要删除选中的 ${this.selectedIds.length} 条记录吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await batchDeletePan123Cache({ ids: this.selectedIds });
          if (response.success) {
            this.$message.success(`成功删除 ${response.data.deletedCount} 条记录`);
            this.fetchCacheList();
            this.selectedIds = [];
          }
        } catch (error) {
          this.$message.error('批量删除失败');
        }
      });
    },
    
    // 统计信息
    async handleStats() {
      this.statsDialogVisible = true;
      this.statsLoading = true;
      try {
        const response = await getPan123CacheStats();
        if (response.success) {
          this.statsData = response.data.stats;
        }
      } catch (error) {
        this.$message.error('获取统计信息失败');
      } finally {
        this.statsLoading = false;
      }
    },
    
    // 复制下载链接
    copyDownloadUrl() {
      if (!this.viewData.download_url) {
        this.$message.warning('下载地址为空');
        return;
      }
      
      // 创建临时文本区域来复制文本
      const textArea = document.createElement('textarea');
      textArea.value = this.viewData.download_url;
      document.body.appendChild(textArea);
      textArea.select();
      
      try {
        document.execCommand('copy');
        this.$message.success('下载链接已复制到剪贴板');
      } catch (err) {
        // 如果浏览器不支持execCommand，尝试使用现代API
        if (navigator.clipboard) {
          navigator.clipboard.writeText(this.viewData.download_url).then(() => {
            this.$message.success('下载链接已复制到剪贴板');
          }).catch(() => {
            this.$message.error('复制失败，请手动复制');
          });
        } else {
          this.$message.error('复制失败，请手动复制');
        }
      } finally {
        document.body.removeChild(textArea);
      }
    },
    
    // 下载文件
    downloadFile() {
      if (!this.viewData.download_url) {
        this.$message.warning('下载地址为空');
        return;
      }
      
      // 创建隐藏的下载链接
      const link = document.createElement('a');
      link.href = this.viewData.download_url;
      link.download = this.viewData.file_name || '下载文件';
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      this.$message.success('开始下载文件');
    },
    
    // 提交表单
    handleSubmit() {
      this.$refs.cacheForm.validate(async (valid) => {
        if (valid) {
          try {
            const data = { ...this.formData };
            // 清理空值
            Object.keys(data).forEach(key => {
              if (data[key] === '' || data[key] === null) {
                delete data[key];
              }
            });
            
            let response;
            if (this.isEdit) {
              response = await updatePan123Cache(this.formData.id, data);
            } else {
              response = await createPan123Cache(data);
            }
            
            if (response.success) {
              this.$message.success(this.isEdit ? '更新成功' : '添加成功');
              this.dialogVisible = false;
              this.fetchCacheList();
            }
          } catch (error) {
            this.$message.error(this.isEdit ? '更新失败' : '添加失败');
          }
        }
      });
    },
    
    // 关闭对话框
    handleDialogClose() {
      this.$refs.cacheForm.resetFields();
      this.dialogVisible = false;
    },
    
    // 格式化时间
    formatTime(time) {
      if (!time) return '-';
      return new Date(time).toLocaleString('zh-CN');
    }
  }
};
</script>

<style scoped>
.cache-management {
  padding: 20px;
}

.operation-bar {
  margin-bottom: 20px;
  padding: 20px;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.table-container {
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 20px;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}

.file-name-text,
.game-title-text {
  display: inline-block;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.delete-button {
  color: #f56c6c;
}

.delete-button:hover {
  color: #f56c6c;
}

.cache-dialog .el-form {
  padding: 0 20px;
}

.view-dialog .el-descriptions {
  margin: 20px 0;
}

.stats-dialog {
  .stats-card {
    text-align: center;
    
    .stats-item {
      padding: 20px;
      
      .stats-number {
        font-size: 32px;
        font-weight: bold;
        color: #409eff;
        margin-bottom: 8px;
      }
      
      .stats-label {
        font-size: 14px;
        color: #606266;
      }
    }
  }
}

.download-url-container {
  position: relative;
}

.url-actions {
  margin-top: 10px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.url-actions .el-button {
  padding: 5px 15px;
}
</style>